import React, { useState } from "react";
import { useMsal } from "@azure/msal-react";
import { useDispatch, useSelector } from "react-redux";
import { ApplicationState } from "../../store";
import { UserLoginState } from '../../store/Login';
import { TeamsState } from "../../store/TeamsState";
import { Invoice, dataActionCreators, TimeSheetEntry } from "../../store/Data";
import { Customer } from "../../interfaces/Customer";
import { Consultant, ConsultantDocument, ConsultantInvoice, ConsultantPeppolReceivedCreditNote, ConsultantPeppolReceivedInvoice, FixePriceConsultant } from "../../interfaces/Consultant";
import { customersActionCreators } from "../../store/customers";
import { ConsultantActionCreators } from '../../store/consultants';
import { ContactsActionCreators } from '../../store/contacts';

export const useTimeSheetEntries = () => {
    const teamsState = useSelector<ApplicationState, TeamsState>((state: ApplicationState) => state.teamsState);
    const { instance } = useMsal();
    const dispatch = useDispatch();
    const timeSheetEntries = useSelector<ApplicationState, TimeSheetEntry[] | undefined>((state: ApplicationState) => state.data.timeSheetEntries);
    const timeSheetEntriesLoading = useSelector((state: ApplicationState) => state.data.timeSheetEntriesLoading);
    React.useEffect(() => {
        if (timeSheetEntries === undefined && timeSheetEntriesLoading === false) {
            dispatch(dataActionCreators.requestTimeSheetEntries(instance, teamsState));
        }
    }, [instance, teamsState, timeSheetEntries, timeSheetEntriesLoading]);
    return { timeSheetEntries, timeSheetEntriesLoading };
};

export const useInvoices = () => {
    const teamsState = useSelector<ApplicationState, TeamsState>((state: ApplicationState) => state.teamsState);
    const { instance } = useMsal();
    const dispatch = useDispatch();
    const invoices = useSelector<ApplicationState, Invoice[] | undefined>((state: ApplicationState) => state.data.invoices);
    const invoicesLoading = useSelector((state: ApplicationState) => state.data.invoicesLoading);
    React.useEffect(() => {
        if (invoices === undefined && invoicesLoading === false) {
            dispatch(dataActionCreators.requestInvoices(instance, teamsState));
        }
    }, [instance, teamsState, invoices, invoicesLoading]);
    return { invoices, invoicesLoading };
};

export const useCustomers = () => {
    const teamsState = useSelector<ApplicationState, TeamsState>((state: ApplicationState) => state.teamsState);
    const { instance } = useMsal();
    const dispatch = useDispatch();
    const customers = useSelector<ApplicationState, Customer[] | undefined>((state: ApplicationState) => state.customersState.customers);
    const customersLoading = useSelector((state: ApplicationState) => state.customersState.customersLoading);
    React.useEffect(() => {
        if (customers === undefined && customersLoading === false) {
            dispatch(customersActionCreators.requestCustomers(instance, teamsState));
        }
    }, [instance, teamsState, customers, customersLoading]);
    return { customers, customersLoading };
};

export const useConsultants = () => {
    const teamsState = useSelector<ApplicationState, TeamsState>((state: ApplicationState) => state.teamsState);
    const { instance } = useMsal();
    const dispatch = useDispatch();
    const consultants = useSelector((state: ApplicationState) => state.consultantsState.consultants);
    const consultantsLoading = useSelector((state: ApplicationState) => state.consultantsState.consultantsLoading);
    React.useEffect(() => {
        if (consultants === undefined && consultantsLoading === false) {
            dispatch(ConsultantActionCreators.requestConsultants(instance, teamsState));
        }
    }, [instance, teamsState, consultants, consultantsLoading]);
    return { consultants, consultantsLoading };
};

export const useConsultantsInvoices = () => {
    const teamsState = useSelector<ApplicationState, TeamsState>((state: ApplicationState) => state.teamsState);
    const { instance } = useMsal();
    const dispatch = useDispatch();
    const consultantsInvoices = useSelector<ApplicationState, ConsultantInvoice[] | undefined>((state: ApplicationState) => state.consultantsState.consultantsInvoices);
    const consultantsInvoicesLoading = useSelector((state: ApplicationState) => state.consultantsState.consultantsInvoicesLoading);
    React.useEffect(() => {
        if (consultantsInvoices === undefined && consultantsInvoicesLoading === false) {
            dispatch(ConsultantActionCreators.requestConsultantsInvoices(instance, teamsState));
        }
    }, [instance, teamsState, consultantsInvoices, consultantsInvoicesLoading]);
    return { consultantsInvoices, consultantsInvoicesLoading };
};

export const useConsultantDocuments = () => {
    const teamsState = useSelector<ApplicationState, TeamsState>((state: ApplicationState) => state.teamsState);
    const { instance } = useMsal();
    const dispatch = useDispatch();
    const consultantDocuments = useSelector<ApplicationState, ConsultantDocument[] | undefined>((state: ApplicationState) => state.consultantsState.consultantDocuments);
    const consultantDocumentsLoading = useSelector((state: ApplicationState) => state.consultantsState.consultantDocumentsLoading);
    React.useEffect(() => {
        if (consultantDocuments === undefined && consultantDocumentsLoading === false) {
            dispatch(ConsultantActionCreators.requestConsultantsDocuments(instance, teamsState));
        }
    }, [instance, teamsState, consultantDocuments, consultantDocumentsLoading]);
    return { consultantDocuments, consultantDocumentsLoading };
};

export const useConsultantPeppolReceivedInvoices = () => {
    const teamsState = useSelector<ApplicationState, TeamsState>((state: ApplicationState) => state.teamsState);
    const { instance } = useMsal();
    const dispatch = useDispatch();
    const consultantPeppolReceivedInvoices = useSelector<ApplicationState, ConsultantPeppolReceivedInvoice[] | undefined>((state: ApplicationState) => state.consultantsState.consultantPeppolReceivedInvoices);
    const consultantPeppolReceivedInvoicesLoading = useSelector((state: ApplicationState) => state.consultantsState.consultantPeppolReceivedInvoicesLoading);
    React.useEffect(() => {
        if (consultantPeppolReceivedInvoices === undefined && consultantPeppolReceivedInvoicesLoading === false) {
            dispatch(ConsultantActionCreators.RequestConsultantPeppolReceivedInvoices(instance, teamsState));
        }
    }, [instance, teamsState, consultantPeppolReceivedInvoices, consultantPeppolReceivedInvoicesLoading]);
    return { consultantPeppolReceivedInvoices, consultantPeppolReceivedInvoicesLoading };
};

export const useConsultantPeppolReceivedCreditNotes = () => {
    const teamsState = useSelector<ApplicationState, TeamsState>((state: ApplicationState) => state.teamsState);
    const { instance } = useMsal();
    const dispatch = useDispatch();
    const consultantPeppolReceivedCreditNotes = useSelector<ApplicationState, ConsultantPeppolReceivedCreditNote[] | undefined>((state: ApplicationState) => state.consultantsState.consultantPeppolReceivedCreditNotes);
    const consultantPeppolReceivedCreditNotesLoading = useSelector((state: ApplicationState) => state.consultantsState.consultantPeppolReceivedCreditNotesLoading);
    React.useEffect(() => {
        if (consultantPeppolReceivedCreditNotes === undefined && consultantPeppolReceivedCreditNotesLoading === false) {
            dispatch(ConsultantActionCreators.RequestConsultantPeppolReceivedCreditNotes(instance, teamsState));
        }
    }, [instance, teamsState, consultantPeppolReceivedCreditNotes, consultantPeppolReceivedCreditNotesLoading]);
    return { consultantPeppolReceivedCreditNotes, consultantPeppolReceivedCreditNotesLoading };
};

export const useProjects = () => {
    const teamsState = useSelector<ApplicationState, TeamsState>((state: ApplicationState) => state.teamsState);
    const { instance } = useMsal();
    const dispatch = useDispatch();
    const projects = useSelector((state: ApplicationState) => state.data.projects);
    const projectsLoading = useSelector((state: ApplicationState) => state.data.projectsLoading);
    React.useEffect(() => {
        if (projects === undefined && projectsLoading === false) {
            dispatch(dataActionCreators.requestProjects(instance, teamsState));
        }
    }, [instance, teamsState, projects, projectsLoading]);
    return { projects, projectsLoading };
};

export const useProjectsAssigments = () => {
    const teamsState = useSelector<ApplicationState, TeamsState>((state: ApplicationState) => state.teamsState);
    const { instance } = useMsal();
    const dispatch = useDispatch();
    const projectsAssigments = useSelector((state: ApplicationState) => state.data.projectsAssigments);
    const projectsAssigmentsLoading = useSelector((state: ApplicationState) => state.data.projectsAssigmentsLoading);
    React.useEffect(() => {
        if (projectsAssigments === undefined && projectsAssigmentsLoading === false) {
            dispatch(dataActionCreators.requestProjectsAssigments(instance, teamsState));
        }
    }, [instance, teamsState, projectsAssigments, projectsAssigmentsLoading]);
    return { projectsAssigments, projectsAssigmentsLoading };
};

export const useProducts = () => {
    const teamsState = useSelector<ApplicationState, TeamsState>((state: ApplicationState) => state.teamsState);
    const { instance } = useMsal();
    const dispatch = useDispatch();
    const products = useSelector((state: ApplicationState) => state.data.products);
    const productsLoading = useSelector((state: ApplicationState) => state.data.productsLoading);
    React.useEffect(() => {
        if (products === undefined && productsLoading === false) {
            dispatch(dataActionCreators.requestProducts(instance, teamsState));
        }
    }, [instance, teamsState, products, productsLoading]);
    return { products, productsLoading };
};

export const useArticles = () => {
    const teamsState = useSelector<ApplicationState, TeamsState>((state: ApplicationState) => state.teamsState);
    const { instance } = useMsal();
    const dispatch = useDispatch();
    const articles = useSelector((state: ApplicationState) => state.data.articles);
    const articlesLoading = useSelector((state: ApplicationState) => state.data.articlesLoading);
    React.useEffect(() => {
        if (articles === undefined && articlesLoading === false) {
            dispatch(dataActionCreators.requestArticles(instance, teamsState));
        }
    }, [instance, teamsState, articles, articlesLoading]);
    return { articles, articlesLoading };
};

export const useSales = () => {
    const teamsState = useSelector<ApplicationState, TeamsState>((state: ApplicationState) => state.teamsState);
    const { instance } = useMsal();
    const dispatch = useDispatch();
    const sales = useSelector((state: ApplicationState) => state.data.sales);
    const salesLoading = useSelector((state: ApplicationState) => state.data.salesLoading);
    React.useEffect(() => {
        if (sales === undefined && salesLoading === false) {
            dispatch(dataActionCreators.requestSells(instance, teamsState));
        }
    }, [instance, teamsState, sales, salesLoading]);
    return { sales, salesLoading };
};

export const useContacts = () => {
    const { isAdmin } = useRigths();
    const teamsState = useSelector<ApplicationState, TeamsState>((state: ApplicationState) => state.teamsState);
    const { instance } = useMsal();
    const dispatch = useDispatch();
    const contacts = useSelector((state: ApplicationState) => state.contactsState.contacts);
    const contactsLoading = useSelector((state: ApplicationState) => state.contactsState.contactsLoading);
    React.useEffect(() => {
        if (isAdmin && contacts === undefined && contactsLoading === false) {
            dispatch(ContactsActionCreators.requestContacts(instance, teamsState));
        }
    }, [isAdmin, instance, teamsState, contacts, contactsLoading]);
    if (!isAdmin) {
        return { contacts: [], contactsLoading: false };
    }
    return { contacts, contactsLoading };
};

export const useFixePriceConsultants = () => {
    const teamsState = useSelector<ApplicationState, TeamsState>((state: ApplicationState) => state.teamsState);
    const { instance } = useMsal();
    const dispatch = useDispatch();
    const fixePriceConsultants = useSelector<ApplicationState, FixePriceConsultant[] | undefined>((state: ApplicationState) => state.consultantsState.fixePriceConsultants);
    const fixePriceConsultantsLoading = useSelector((state: ApplicationState) => state.consultantsState.fixePriceConsultantsLoading);
    React.useEffect(() => {
        if (fixePriceConsultants === undefined && fixePriceConsultantsLoading === false) {
            dispatch(ConsultantActionCreators.requestFixePriceConsultants(instance, teamsState));
        }
    }, [instance, teamsState, fixePriceConsultants, fixePriceConsultantsLoading]);
    return { fixePriceConsultants, fixePriceConsultantsLoading };
};

export const useRigths = () => {
    const UserLoginState = useSelector<ApplicationState, UserLoginState>((state: ApplicationState) => state.userNeomyticLogin);
    return {
        isAdmin: UserLoginState && UserLoginState.rigths && UserLoginState.rigths.isAdmin,
        canEnterTimeSheet: UserLoginState && UserLoginState.rigths && UserLoginState.rigths.canEnterTimeSheet,
        canEnterDocuments: UserLoginState && UserLoginState.rigths && UserLoginState.rigths.canEnterDocuments,
        canManageConsultantDocuments: UserLoginState && UserLoginState.rigths && UserLoginState.rigths.canEnterDocuments,
        seeDebugInfo: UserLoginState && UserLoginState.rigths && UserLoginState.rigths.seeDebugInfo,
        consultantid: UserLoginState && UserLoginState.rigths && UserLoginState.rigths.consultantid,
        version: UserLoginState && UserLoginState.rigths && UserLoginState.rigths.version ? UserLoginState.rigths.version : undefined,
        buildTime: UserLoginState && UserLoginState.rigths && UserLoginState.rigths.buildTime ? UserLoginState.rigths.buildTime : undefined,
    };
};

export const useLoggedConsultant = () => {
    const { consultants } = useConsultants();
    const { consultantid } = useRigths();
    const [loggedConsultant, setLoggedConsultant] = useState<Consultant>();
    React.useEffect(() => {
        setLoggedConsultant(consultants && consultants.length === 1
            ? consultants[0]
            : (consultants && consultantid
                ?
                consultants.find(c => consultantid !== undefined && c.consultantid === consultantid)
                :
                undefined
            ));
    }, [consultants, consultantid]);
    return { loggedConsultant };
};
